import { PromptAddendumModel } from "./getLandlordResearchPrompt";
import { PeopleRole, taskForRole } from "../models/peopleCandidate";
import axiosConfig from "./axiosConfig";

// One revision of a role's editable people-ranking prompt. Mirrors
// LandlordPromptRevision - the built-in seed is revision 1 (isSeed=true, no DB
// row); operator saves climb from 2. Reuses PromptAddendumModel + PromptRuleModel
// (the on-the-wire snake_case rule shape) from the landlord prompt.
export interface PeoplePromptRevision {
  revisionNumber: number;
  // The compiled render (what the ranking pass runs on); `addendum` is its source.
  promptText: string;
  addendum: PromptAddendumModel;
  ruleCount: number;
  // Human summary of what this revision changed (auto-derived for per-rule edits).
  note: string | null;
  createdBy: string;
  createdAt: string | null;
  isActive: boolean;
  isSeed?: boolean;
}

// The whole editable people-ranking prompt for one role: active revision + full
// history + the non-editable enforced footer. Mirrors the landlord prompt-data
// shape; the people BE additionally echoes the `role` and a `revisionsTruncated`
// flag (the history is capped, older revisions stay restorable/diffable by number).
export interface PeopleRankingPromptData {
  // The prompt task_key the BE echoes ("people_landlord" | "people_tenant"). The card
  // keys its own state on the `role` prop it holds, not on this.
  task: string;
  // The revision the ranking pass currently runs (a saved one, or the seed).
  active: PeoplePromptRevision;
  // The shipped built-in prompt text, for a "reset to default" action.
  defaultSeed: string;
  // The shipped built-in prompt as an addendum (whole text as the preamble, no
  // rules), for resetting the structured editor.
  defaultSeedAddendum: PromptAddendumModel;
  // The non-editable answer-shape + data-residency guardrail appended to whatever
  // prompt is active. Returned so the card can show it read-only.
  enforcedFooter: string;
  // Recent history, newest first, ending with the synthetic seed (revision 1).
  revisions: PeoplePromptRevision[];
  // True when older revisions were dropped past the history cap.
  revisionsTruncated: boolean;
}

const getPeopleRankingPrompt = async (
  authToken: string,
  role: PeopleRole
): Promise<PeopleRankingPromptData> => {
  const { data } = await axiosConfig.get(`/leads/ai-prompt`, {
    params: { task: taskForRole(role) },
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};

export default getPeopleRankingPrompt;
